import type { LandingContent } from './types';

export const en: LandingContent = {
  locale: 'en',
  seo: {
    title: 'Maxim Domentii | Software Architect, SaaS & AI Automation Consultant in Switzerland',
    description:
      'Senior software engineer in Switzerland helping SMEs, founders and product teams build SaaS products, cloud-native systems, websites, AI agents and modern digital workflows.',
    ogTitle: 'Software Architecture, SaaS Development & AI Automation in Switzerland',
    ogDescription:
      'Work with Maxim Domentii to design, build and scale reliable software, cloud platforms, business websites and AI-enabled workflows.',
  },
  nav: {
    services: 'Services',
    solutions: 'Solutions',
    process: 'Process',
    experience: 'Experience',
    faq: 'FAQ',
    contact: 'Contact',
    linkedin: 'LinkedIn',
    menu: 'Open navigation menu',
    close: 'Close navigation menu',
  },
  hero: {
    eyebrow: 'Zurich-based independent software consultant',
    h1: 'I design and build cloud-native software, SaaS products and AI-enabled business workflows.',
    lead: 'I help Swiss SMEs, founders and product teams move from idea to production with software architecture, full-stack development, DevOps and pragmatic AI automation.',
    primaryCta: 'Discuss your project',
    secondaryCta: 'View services',
    trustBullets: [
      '10+ years in software engineering',
      'Senior engineer in Swiss banking environments',
      'AWS Certified Cloud Practitioner',
      'Java, Spring Boot, React, Kubernetes, Kafka',
    ],
    visualAlt:
      'Architecture map connecting SaaS product, cloud platform and AI workflow modules.',
  },
  trustBar: {
    items: [
      { value: '2015', label: 'Software engineering career started' },
      { value: '10+', label: 'Years building production systems' },
      { value: 'AWS', label: 'Certified Cloud Practitioner' },
      { value: 'CH', label: 'Based in the Zurich area' },
    ],
  },
  services: {
    eyebrow: 'Services',
    h2: 'One partner for strategy, architecture and delivery.',
    intro:
      'From a new business identity to a production-grade SaaS platform, I combine engineering discipline with practical product thinking and modern AI tooling.',
    items: [
      {
        id: 'launch',
        title: 'Business Launch & Digital Identity',
        description:
          'Launch or rebrand your company with the essentials in one coherent package: logo direction, business cards, domain, business email, website, contact form and optional AI assistant workflows.',
        deliverables: [
          'Brand direction',
          'Domain and email setup',
          'Responsive website',
          'SEO basics',
          'Contact workflow',
          'AI support options',
        ],
      },
      {
        id: 'saas',
        title: 'SaaS Product Design & Implementation',
        description:
          'Turn a product idea into a working SaaS MVP or production application with a clear architecture, maintainable codebase and scalable delivery plan.',
        deliverables: [
          'Product discovery',
          'Frontend',
          'Backend',
          'APIs',
          'Authentication strategy',
          'Database design',
          'Deployment pipeline',
        ],
      },
      {
        id: 'architecture',
        title: 'Software Architecture & Modernization',
        description:
          'Design robust systems, improve existing platforms and make technical decisions that reduce risk while keeping delivery moving.',
        deliverables: [
          'Architecture reviews',
          'System design',
          'Microservices',
          'Integration patterns',
          'Performance improvements',
          'Technical roadmaps',
        ],
      },
      {
        id: 'ai',
        title: 'AI Agents & Automation',
        description:
          'Apply AI where it creates measurable value: customer intake, internal knowledge workflows, lead qualification, operations support and developer productivity.',
        deliverables: [
          'AI workflow design',
          'Agent prototypes',
          'Prompt and tool design',
          'Integrations',
          'Human-in-the-loop review',
          'Safe rollout plans',
        ],
      },
      {
        id: 'cloud',
        title: 'Cloud, DevOps & CI/CD',
        description:
          'Deploy and operate applications with automated pipelines, containerized infrastructure and cloud practices that support reliability and fast iteration.',
        deliverables: [
          'Docker',
          'Kubernetes',
          'CI/CD',
          'Cloud deployments',
          'Observability basics',
          'Release process improvements',
        ],
      },
      {
        id: 'consulting',
        title: 'Software & Digital Consulting',
        description:
          'Get senior engineering guidance before committing budget to a vendor, platform, architecture or implementation path.',
        deliverables: [
          'Technical discovery',
          'Vendor evaluation',
          'Feasibility checks',
          'Implementation planning',
          'Architecture documentation',
        ],
      },
    ],
  },
  packages: {
    h2: 'Choose the starting point that fits your business.',
    items: [
      {
        title: 'Launch Package',
        audience:
          'For new companies, freelancers and small businesses that need a professional digital foundation.',
        includes: [
          'Brand basics',
          'Domain',
          'Business email',
          'Landing page',
          'Legal-page structure',
          'Contact form',
          'SEO setup',
          'Deployment',
        ],
      },
      {
        title: 'SaaS MVP Package',
        audience: 'For founders and teams that need a working product, not just a prototype.',
        includes: [
          'Discovery workshop',
          'Architecture',
          'UI',
          'API',
          'Database',
          'Authentication plan',
          'Deployment',
          'Iteration roadmap',
        ],
      },
      {
        title: 'AI Workflow Package',
        audience:
          'For businesses that want to automate repetitive work without losing control of quality.',
        includes: [
          'Workflow mapping',
          'Automation design',
          'AI agent prototype',
          'Integrations',
          'Safeguards',
          'Training',
          'Handover',
        ],
      },
    ],
  },
  process: {
    h2: 'A delivery process built for clarity.',
    steps: [
      {
        title: 'Discover',
        text: 'We clarify the business goal, users, constraints, risks and success criteria before writing code.',
      },
      {
        title: 'Design',
        text: 'I define the user flow, architecture, data model and delivery plan so implementation decisions stay aligned.',
      },
      {
        title: 'Build',
        text: 'I implement the solution with clean code, modern tooling, automated checks and frequent demos.',
      },
      {
        title: 'Launch',
        text: 'We deploy, validate, monitor and prepare the next iteration with practical documentation.',
      },
    ],
  },
  experience: {
    h2: 'Senior engineering experience in demanding environments.',
    body: 'Since 2015, I have worked across banking, cloud platforms, web applications and digital transformation projects. My background includes senior engineering roles in Zurich, cloud-based real-time data applications, investment content platforms, reporting systems, SSL certificate platforms and internet banking solutions.',
    highlights: [
      'Led developers delivering a cloud-based real-time cash flow application',
      'Designed algorithms and scalable backend services',
      'Built and improved CI/CD pipelines',
      'Worked with Java, Spring Boot, React, Kafka, Kubernetes, Docker and SQL',
      'Coached developers, supported production releases and improved agile delivery processes',
    ],
    timelineTitle: 'Professional timeline',
    timeline: [
      {
        period: 'Feb 2022 - present',
        role: 'Senior Software Engineer',
        organization: 'UBS AG, Zurich',
        context: 'Senior engineering in major Swiss banking environments.',
      },
      {
        period: 'May 2021 - Jan 2022',
        role: 'Senior Software Engineer',
        organization: 'Cognizant Technology Solutions AG for Credit Suisse',
        context: 'Delivery for banking platforms and digital workflows.',
      },
      {
        period: 'Dec 2019 - Apr 2021',
        role: 'Senior Software Engineer',
        organization: 'EPAM Systems Switzerland for UBS',
        context: 'Cloud-based real-time data applications and platform delivery.',
      },
      {
        period: 'Aug 2018 - Nov 2019',
        role: 'Senior Software Developer',
        organization: 'RINF TECH for 1&1 IONOS',
        context: 'SSL certificate platforms and backend engineering.',
      },
      {
        period: 'Feb 2016 - Jul 2018',
        role: 'Software Developer',
        organization: 'UniCredit Services',
        context: 'Internet banking solutions and reporting systems.',
      },
      {
        period: 'Sep 2012 - Jul 2016',
        role: 'Computer Science',
        organization: 'Polytechnic University of Bucharest',
        context: 'Computer science education with engineering fundamentals.',
      },
    ],
  },
  ai: {
    h2: 'AI-aware engineering without the hype.',
    body: 'I use modern AI coding copilots and structured engineering workflows to accelerate delivery while keeping architecture, code quality and security under human control. I have also contributed to training and evaluating leading AI models, giving me a practical understanding of how AI systems behave, where they help and where safeguards are needed.',
    bullets: [
      'AI-assisted implementation with senior engineering review',
      'Automation ideas grounded in real business workflows',
      'Human-in-the-loop design for critical decisions',
      'Clear boundaries around data privacy, quality and maintainability',
    ],
  },
  testimonials: {
    h2: 'What colleagues say',
    items: [
      {
        quote: 'Max is a bright and result-driven guy',
        author: 'Mihai Lacatusu',
        role: 'Head of Domains & SSL Engineering at 1&1 IONOS',
      },
      {
        quote: 'Max demonstrated strong conceptual skills...',
        author: 'Martynas Gaurilcikas',
        role: 'Business Analyst',
      },
      {
        quote: 'His mind is about clarity and this could also be seen in his code',
        author: 'Lucian Ilie',
        role: 'Software Engineer',
      },
    ],
    cta: 'View LinkedIn recommendations',
  },
  faq: {
    h2: 'Frequently asked questions',
    items: [
      {
        question: 'Can you help with both strategy and implementation?',
        answer:
          'Yes. I can start with discovery and architecture, then continue into full-stack implementation, deployment and iteration.',
      },
      {
        question: 'Do you work with small businesses as well as product teams?',
        answer:
          'Yes. I work with Swiss SMEs, founders, freelancers and product teams that need senior technical execution without building a full internal team first.',
      },
      {
        question: 'Can you build a complete website and business setup?',
        answer:
          'Yes. For new companies or rebrands I can support the digital foundation: domain, business email, landing page, contact workflow, SEO basics and optional AI automations.',
      },
      {
        question: 'Can you modernize an existing application?',
        answer:
          'Yes. I can review the current architecture, identify risks, simplify delivery and implement targeted improvements.',
      },
      {
        question: 'Which technologies do you usually work with?',
        answer:
          'My core stack includes Java, Spring Boot, React, Node.js, Kafka, Docker, Kubernetes, SQL, Git and CI/CD pipelines. I choose technology based on business fit, not trends alone.',
      },
    ],
  },
  contact: {
    h2: 'Tell me what you want to build.',
    intro:
      'Share a few details about your project, business goal or technical challenge. I will get back to you with the next practical step.',
    directTitle: 'Direct contact',
    labels: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone number',
      text: 'Tell me about your project',
    },
    placeholders: {
      name: 'Max Mustermann',
      email: 'max@example.com',
      phone: '+41 77 000 00 00',
      text: 'Hi Maxim, I need help launching a SaaS MVP for...',
    },
    submit: 'Send message',
    sending: 'Sending...',
    success: 'Message sent. Thank you. I will contact you soon.',
    failure:
      'Something went wrong. Please try again later or contact me directly by email.',
    recaptchaDisclosure:
      'This form is protected by Google reCAPTCHA. See the data protection page for details.',
    validation: {
      name: 'Please enter at least 2 characters.',
      email: 'Please enter a valid email address.',
      phone: 'Please enter a valid phone number or leave this field empty.',
      textMin: 'Please enter at least 40 characters.',
      textMax: 'Please keep the message under 5000 characters.',
      token: 'Please complete the reCAPTCHA check.',
    },
  },
  footer: {
    summary:
      'Senior software engineer and AI-ready software consultant helping Swiss businesses launch, automate and scale digital products.',
    legalNotice: 'Legal notice',
    dataProtection: 'Data protection',
    language: 'Language',
    copyright: 'All rights reserved.',
  },
  common: {
    deliverables: 'Deliverables',
    includes: 'Includes',
    email: 'Email',
    phone: 'Phone',
    externalLink: 'Opens an external website',
  },
};
